import { HexColor, Idea } from "../model/types"

export type IdeaColourResolver = (idea: Idea) => HexColor
export type IdeaColourMode =
  | "default"
  | "creationTime"
  | "editTime"
  | "lastViewedTime"
  | "self"

export const defaultIdeaColourResolver: IdeaColourResolver = (idea) => idea.colour

// Bright warm yellow (readable on white, not neon) vs dim blue-charcoal for the rest.
const SELF_SOURCE_COLOUR = "#e8c547" as HexColor
const NON_SELF_SOURCE_COLOUR = "#3f4b5c" as HexColor

export const selfIdeaColourResolver: IdeaColourResolver = (idea) =>
  idea.sourceType === "self" ? SELF_SOURCE_COLOUR : NON_SELF_SOURCE_COLOUR

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

const componentToHex = (component: number): string =>
  Math.round(component).toString(16).padStart(2, "0")

const interpolate = (from: number, to: number, ratio: number): number => from + (to - from) * ratio

const parseHex = (hex: HexColor): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
]

const mixHex = (from: HexColor, to: HexColor, ratio: number): HexColor => {
  const t = clamp(ratio, 0, 1)
  const [r1, g1, b1] = parseHex(from)
  const [r2, g2, b2] = parseHex(to)
  return `#${componentToHex(interpolate(r1, r2, t))}${componentToHex(
    interpolate(g1, g2, t),
  )}${componentToHex(interpolate(b1, b2, t))}` as HexColor
}

const hslToHex = (hue: number, saturation: number, lightness: number): HexColor => {
  hue = ((hue % 360) + 360) % 360

  const s = saturation / 100
  const l = lightness / 100
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
  const m = l - c / 2

  let r = 0
  let g = 0
  let b = 0

  if (hue < 60) {
    r = c
    g = x
  } else if (hue < 120) {
    r = x
    g = c
  } else if (hue < 180) {
    g = c
    b = x
  } else if (hue < 240) {
    g = x
    b = c
  } else if (hue < 300) {
    r = x
    b = c
  } else {
    r = c
    b = x
  }

  return `#${componentToHex((r + m) * 255)}${componentToHex((g + m) * 255)}${componentToHex(
    (b + m) * 255,
  )}` as HexColor
}

const timestampFromDate = (date: Date | string | null | undefined): number | null => {
  if (!date) return null

  const value = date instanceof Date ? date.getTime() : new Date(date).getTime()

  return Number.isFinite(value) ? value : null
}

// Muted slate→terracotta for older ages; recent end uses the self-highlight gold.
const HEATMAP_SATURATION = 48
const HEATMAP_LIGHTNESS = 52

// Older history: slate-blue → muted red (via soft violet).
const HEATMAP_OLD_HUE = 215
const HEATMAP_RED_HUE = 360

const spectrumColour = (ratio: number): HexColor => {
  const hue = HEATMAP_OLD_HUE + (HEATMAP_RED_HUE - HEATMAP_OLD_HUE) * clamp(ratio, 0, 1)
  return hslToHex(hue, HEATMAP_SATURATION, HEATMAP_LIGHTNESS)
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

const CREATION_RECENT_RED_AGE_DAYS = 30

// One-month boundary: same muted red as the old-spectrum hot end.
const HEATMAP_RECENT_RED = spectrumColour(1)

// Recent month: self-highlight gold → amber → muted red (RGB mix keeps gold bright).
const CREATION_RECENT_COLOUR_STOPS: { ageDays: number; colour: HexColor }[] = [
  { ageDays: 0, colour: SELF_SOURCE_COLOUR },
  { ageDays: 7, colour: mixHex(SELF_SOURCE_COLOUR, HEATMAP_RECENT_RED, 0.28) },
  { ageDays: CREATION_RECENT_RED_AGE_DAYS, colour: HEATMAP_RECENT_RED },
]

const creationRecentColourForAge = (ageDays: number): HexColor => {
  const oldestStop = CREATION_RECENT_COLOUR_STOPS[CREATION_RECENT_COLOUR_STOPS.length - 1]
  const clampedAgeDays = clamp(ageDays, 0, oldestStop.ageDays)
  const nextStopIndex = CREATION_RECENT_COLOUR_STOPS.findIndex(
    (stop) => clampedAgeDays <= stop.ageDays,
  )

  if (nextStopIndex <= 0) return CREATION_RECENT_COLOUR_STOPS[0].colour

  const previousStop = CREATION_RECENT_COLOUR_STOPS[nextStopIndex - 1]
  const nextStop = CREATION_RECENT_COLOUR_STOPS[nextStopIndex]
  const ratio = (clampedAgeDays - previousStop.ageDays) / (nextStop.ageDays - previousStop.ageDays)

  return mixHex(previousStop.colour, nextStop.colour, ratio)
}

// Charcoal grey: readable on light paper and on the dark-mode navy grid.
const LAST_VIEWED_NEVER_COLOUR = "#4b5563" as HexColor

const neverViewedIdeaColourResolver: IdeaColourResolver = () => LAST_VIEWED_NEVER_COLOUR

const recentAgeExtendedIdeaColourResolver = (
  ideas: Iterable<Idea>,
  timestampForIdea: (idea: Idea) => number | null,
  fallbackResolver: IdeaColourResolver,
  now: Date = new Date(),
): IdeaColourResolver => {
  const timestamps = Array.from(ideas)
    .map(timestampForIdea)
    .filter((timestamp): timestamp is number => timestamp !== null)

  if (timestamps.length === 0) return fallbackResolver

  const nowTimestamp = timestampFromDate(now) ?? Date.now()
  const oldest = Math.min(...timestamps)
  const recentRedTimestamp = nowTimestamp - CREATION_RECENT_RED_AGE_DAYS * MS_PER_DAY

  return (idea: Idea): HexColor => {
    const timestamp = timestampForIdea(idea)
    if (timestamp === null) return fallbackResolver(idea)

    const ageDays = (nowTimestamp - timestamp) / MS_PER_DAY
    if (ageDays <= CREATION_RECENT_RED_AGE_DAYS) return creationRecentColourForAge(ageDays)
    if (recentRedTimestamp === oldest) return spectrumColour(1)

    const ratio = (timestamp - oldest) / (recentRedTimestamp - oldest)
    return spectrumColour(ratio)
  }
}

export const creationTimeIdeaColourResolver = (
  ideas: Iterable<Idea>,
  now: Date = new Date(),
): IdeaColourResolver =>
  recentAgeExtendedIdeaColourResolver(
    ideas,
    (idea) => timestampFromDate(idea.createdAt),
    defaultIdeaColourResolver,
    now,
  )

export const editTimeIdeaColourResolver = (
  ideas: Iterable<Idea>,
  now: Date = new Date(),
): IdeaColourResolver =>
  recentAgeExtendedIdeaColourResolver(
    ideas,
    (idea) => timestampFromDate(idea.editedAt || idea.updatedAt || idea.createdAt),
    defaultIdeaColourResolver,
    now,
  )

export const lastViewedTimeIdeaColourResolver = (
  ideas: Iterable<Idea>,
  now: Date = new Date(),
): IdeaColourResolver =>
  recentAgeExtendedIdeaColourResolver(
    ideas,
    (idea) => timestampFromDate(idea.lastViewedAt),
    neverViewedIdeaColourResolver,
    now,
  )
